/**
 * useFavoriteKuIds — Renvoie un Set des IDs de KnowledgeUnits favorites
 * de l'utilisateur courant, avec re-sync automatique sur événement
 * `favorites:updated`.
 *
 * Vincent B614 (Eric 2026-07-29 SPEC STRICK) — Utilisé par les panels
 * Q&R / Scripts / Procédures / Knowledge (Objections/Concepts) pour
 * afficher le ♥ favori sur chaque KU dans l'onglet « Modifier ».
 *
 * Une seule requête `/api/favorites` au montage + rafraîchissement sur
 * événement (au lieu d'une requête par ♥). L'axios interceptor de
 * AuthContext émet déjà cet événement à chaque POST/DELETE de favori.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE as API } from "../lib/apiBase";
import { cachedGet, invalidateCache } from "../lib/apiCache";

export function useFavoriteKuIds() {
  const [favIds, setFavIds] = useState(new Set());

  const reload = useCallback(async (force = false) => {
    try {
      // B1085 — Cache partagé : cette liste était demandée une deuxième fois
      // sur la page vidéo, en plus de celle de l'entête.
      if (force) invalidateCache("/api/favorites");
      const { data } = await cachedGet(axios, `${API}/api/favorites`, { ttl: 30000 });
      const list = Array.isArray(data) ? data : [];
      // Vincent B856 (Eric 2026-02-14 SPEC STRICK PROD) — Élargi le
      // filtre au type `sales_script` (les scripts sont dans leur
      // propre collection et sont favorisés avec item_type="sales_script",
      // cf. ScriptsPage.js L147). Sans ça, un script marqué ♥
      // n'apparaissait jamais dans l'onglet « Mes favoris » (B849)
      // ni sur son propre ♥ après refresh (B614).
      const ACCEPTED_TYPES = new Set(["knowledge", "sales_script"]);
      setFavIds(new Set(list.filter((f) => ACCEPTED_TYPES.has(f.item_type)).map((f) => f.item_id)));
    } catch (_) {
      /* silencieux — le ♥ s'affichera outline en cas d'erreur réseau */
    }
  }, []);

  useEffect(() => {
    reload();
    const onFavUpdated = () => reload(true);
    window.addEventListener("favorites:updated", onFavUpdated);
    return () => window.removeEventListener("favorites:updated", onFavUpdated);
  }, [reload]);

  return favIds;
}
